import * as React from 'react';
import {
    Show,
    useRecordContext,
    TextField,
    List,
    Datagrid,
    useDataProvider,
    SimpleShowLayout,
    RichTextField,
    DataProviderContext,
    useRefresh,
} from 'react-admin';
import { Alert, Button, CircularProgress, Box } from '@mui/material';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { useParams } from 'react-router';
import { createCustomGoDataProvider, goApi } from '../../services';
import { Batch_Templates } from '../../custom';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import StatusTags from '../../common/StatusTags';
import { apiRoutes } from '../../services/apiRoutes';

const TemplateSubjectsContext = React.createContext<{
    subjects: any[];
    refetch: () => void;
}>({ subjects: [], refetch: () => {} });

const useActionHandler = (apiCall: () => Promise<any>, onSuccess: () => void) => {
    const [isLoading, setIsLoading] = React.useState(false);
    const refresh = useRefresh();

    const handleAction = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            await apiCall();
            onSuccess();
            refresh();
        } catch (error) {
            console.error('Action failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return { handleAction, isLoading };
};

const SubjectAction = () => {
    const record = useRecordContext();
    const { id: batchTemplateId } = useParams<{ id: string }>();
    const { subjects, refetch } = React.useContext(TemplateSubjectsContext);
    const attachedSubject = subjects.find((s: any) => s.subjectId === record.id);

    const addAction = useActionHandler(
        () =>
            goApi.post(apiRoutes.BATCH_TEMPLATE_SUBJECTS, {
                batchTemplateId,
                subjectId: record.id,
            }),
        refetch,
    );

    const removeAction = useActionHandler(
        () => goApi.delete(`${apiRoutes.BATCH_TEMPLATE_SUBJECTS}/${attachedSubject?.id}/remove`),
        refetch,
    );

    return (
        <div>
            {attachedSubject ? (
                <Button
                    startIcon={<RemoveCircle />}
                    onClick={removeAction.handleAction}
                    disabled={removeAction.isLoading}
                    color="error"
                >
                    Remove
                </Button>
            ) : (
                <Button
                    startIcon={<AddCircle />}
                    onClick={addAction.handleAction}
                    disabled={addAction.isLoading}
                >
                    Attach
                </Button>
            )}
        </div>
    );
};

const BatchTemplateSubjects = () => {
    const record = useRecordContext();
    const { id: batchTemplateId } = useParams<{ id: string }>();
    const baseDataProvider = useDataProvider();
    const [subjects, setSubjects] = React.useState<any[]>([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [refetchKey, setRefetchKey] = React.useState(0);

    React.useEffect(() => {
        if (!batchTemplateId) return;

        setIsLoading(true);
        goApi
            .get(`${apiRoutes.BATCH_TEMPLATE_SUBJECTS}?batchTemplateId=${batchTemplateId}&limit=50`)
            .then(res => setSubjects(res?.data?.items || []))
            .catch(err => console.error('Failed to fetch subjects:', err))
            .finally(() => setIsLoading(false));
    }, [batchTemplateId, refetchKey]);

    const customDataProvider = React.useMemo(
        () =>
            createCustomGoDataProvider(baseDataProvider, [
                { resource: 'subjects', apiEndpoint: '/subjects' },
            ]),
        [baseDataProvider],
    );

    return (
        <TemplateSubjectsContext.Provider
            value={{ subjects, refetch: () => setRefetchKey(k => k + 1) }}
        >
            <Alert severity="info" sx={{ mb: 1 }}>
                Subjects listed under the same program as this batch template
            </Alert>

            <DataProviderContext.Provider value={customDataProvider}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={5}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List
                        actions={false}
                        resource="subjects"
                        component="div"
                        filter={{ programId: record.programId }}
                        queryOptions={{ refetchOnWindowFocus: false, refetchOnReconnect: false }}
                    >
                        <Datagrid bulkActionButtons={false}>
                            <SubjectAction />
                            <TextField source="name" label="Subject Name" />
                            <StatusTags source="status" label="Status" />
                        </Datagrid>
                    </List>
                )}
            </DataProviderContext.Provider>
        </TemplateSubjectsContext.Provider>
    );
};

const BatchTemplateTitle = () => {
    const record = useRecordContext<Batch_Templates>();
    return record?.title ? <span>{record?.title}</span> : null;
};

const TAB_CONFIG = (id: string) => [
    {
        label: 'Details',
        content: (
            <Show
                actions={<></>}
                title={<BatchTemplateTitle />}
                resource="batch-templates"
                id={id}
                queryOptions={{ refetchOnWindowFocus: false, refetchOnReconnect: false }}
            >
                <SimpleShowLayout>
                    <TextField source="title" />
                    <TextField source="slug" />
                    <TextField source="type" />
                    <TextField source="status" />
                    <RichTextField source="description" />
                    <FormattedDateTimeField source="createdAt" />
                </SimpleShowLayout>
            </Show>
        ),
    },
    {
        label: 'Subjects',
        content: (
            <Show
                title={<></>}
                actions={<></>}
                resource="batch-templates"
                id={id}
                queryOptions={{ refetchOnWindowFocus: false, refetchOnReconnect: false }}
            >
                <SimpleShowLayout>
                    <BatchTemplateSubjects />
                </SimpleShowLayout>
            </Show>
        ),
    },
];

const BatchTemplateShow = () => {
    const [activeTab, setActiveTab] = React.useState(0);
    const { id } = useParams<{ id: string }>();
    const tabs = React.useMemo(() => TAB_CONFIG(id!), [id]);

    return (
        <div className="rounded-md border my-6">
            <div className="border-b py-2 flex text-lg flex-row gap-16 px-10 mb-6 rounded-t-md bg-white">
                {tabs.map((tab, idx) => (
                    <div
                        key={tab.label}
                        onClick={() => setActiveTab(idx)}
                        className={`hover:cursor-pointer ${
                            idx === activeTab
                                ? 'text-[#4f3cc9] underline decoration-4 underline-offset-[10px]'
                                : ''
                        }`}
                    >
                        {tab.label}
                    </div>
                ))}
            </div>
            <div className="tab-content bg-white px-2 pb-6">{tabs[activeTab].content}</div>
        </div>
    );
};

export default BatchTemplateShow;
